<template>
	<div>
		<TypographyTitle :level="3" :class="titleClass" :aria-label="$t(titleKey)">
			{{ $t(titleKey) }}
			<QuestionCircleFilled
				v-if="hasDescription && isDescriptionExpanded"
				class="chart-title-hint-icon chart-title-hint-icon--active"
				@click="toggleDescription"
			/>
			<QuestionCircleOutlined
				v-else-if="hasDescription"
				class="chart-title-hint-icon"
				@click="toggleDescription"
			/>
		</TypographyTitle>
		<div
			v-if="hasDescription"
			class="chart-description-collapse"
			:class="{ 'chart-description-collapse--expanded': isDescriptionExpanded }"
		>
			<div class="chart-description-collapse__inner" :class="descriptionClass">
				<div class="chart-description-content">
					<slot name="description">
						<TypographyParagraph v-if="descriptionKey" class="chart-description-text">
							{{ $t(descriptionKey) }}
						</TypographyParagraph>
					</slot>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { ref, computed } from 'vue';
import type { Slots } from 'vue';
import { TypographyTitle, TypographyParagraph } from 'ant-design-vue';
import { QuestionCircleOutlined, QuestionCircleFilled } from '@ant-design/icons-vue';

export default {
	components: {
		TypographyTitle,
		TypographyParagraph,
		QuestionCircleOutlined,
		QuestionCircleFilled,
	},
	props: {
		titleKey: {
			type: String,
			required: true,
		},
		descriptionKey: {
			type: String,
			default: '',
		},
		titleClass: {
			type: String,
			default: 'pd-lg-x',
		},
		descriptionClass: {
			type: String,
			default: 'pd-lg-x',
		},
	},
	setup(props, { slots }: { slots: Slots }) {
		const isDescriptionExpanded = ref(false);

		const hasDescription = computed(() =>
			Boolean(props.descriptionKey) || Boolean(slots.description),
		);

		const toggleDescription = () => {
			isDescriptionExpanded.value = !isDescriptionExpanded.value;
		};

		return {
			isDescriptionExpanded,
			hasDescription,
			toggleDescription,
		};
	},
};
</script>

<style scoped>
.chart-title-hint-icon {
	margin-left: 8px;
	font-size: 14px;
	color: rgba(0, 0, 0, 0.45);
	cursor: pointer;
	vertical-align: middle;
	transition: color 0.2s ease;
}

.chart-title-hint-icon--active {
	color: rgba(0, 0, 0, 0.88);
}

.chart-description-collapse {
	display: grid;
	grid-template-rows: 0fr;
	transition: grid-template-rows 0.2s ease;
}

.chart-description-collapse--expanded {
	grid-template-rows: 1fr;
}

.chart-description-collapse__inner {
	overflow: hidden;
}

.chart-description-text {
	margin-bottom: 0;
	color: rgba(0, 0, 0, 0.88);
}

.chart-description-content :deep(.ant-typography) {
	margin-bottom: 0;
	color: rgba(0, 0, 0, 0.88);
}

.chart-description-content :deep(.ant-typography + .ant-typography) {
	margin-top: 8px;
}
</style>
